// CRM / Module 5 data layer — Firebase RTDB CRUD for customers, parts,
// quotes, orders, and quote templates.
//
// Mirrors the patterns in src/shared/brainMemory.ts:
//   - All async; await `awaitFirebaseUser()` before any read/write
//   - All writes go through `stripUndefinedDeep` + `assertFiniteNumbers`
//     so Firebase never sees undefined / NaN / Infinity (it rejects all
//     three with opaque INVALID_DATA errors — we throw earlier with a
//     readable field path)
//   - `push()` generates the Firebase key (currently used as the entity id)
//   - Baseline fields (createdAt/updatedAt/_v/_audit/_deleted) auto-populated
//   - createX writes _audit[0] = {action:"create"}; updateX appends an
//     update entry; status-transition helpers append a status_change entry
//   - Indexes declared in firebase.rules.json
//
// See spec/14-module-crm.md + spec/20-data-model.md for the canonical model.
// Field names + enum values come straight from spec/14 — UI agents, the PDF
// exporter, and the v1->v2 migration all key off these names.
//
// Audit entries record the signed-in Firebase user's uid. The AuthGate in
// App.tsx prevents the SDK from being called pre-auth in the browser, so
// currentFirebaseUser() is reliably non-null at every UI call site. The
// 'system' fallback survives only for server-side scripts (migrate-v1,
// push-migrated-customers, battle-test) that run outside the browser and
// may not have a session — rare and acceptable.

import {
  ref,
  push,
  set,
  get,
  query,
  orderByChild,
  equalTo,
  type DatabaseReference,
} from 'firebase/database';
import { db, awaitFirebaseUser, currentFirebaseUser } from './firebase';
import type {
  Customer,
  CustomerTier,
  Part,
  Quote,
  QuoteStatus,
  Order,
  OrderStatus,
  QuoteTemplate,
  BaselineFields,
  AuditEntry,
} from './crmTypes';
import {
  stripUndefinedDeep,
  assertFiniteNumbers,
  assertIsoDate,
  assertQuoteTransition,
  assertOrderTransition,
  assertPartIdsSpecsConsistent,
} from './crmValidators';

// =====================================================
// Local helpers
// =====================================================

/**
 * Sanitize a payload for a Firebase write. Single chokepoint that runs both
 * the recursive undefined-strip and the finite-number assertion. Every
 * createX / updateX / transition / softDelete / restore goes through this so
 * Firebase never sees undefined / NaN / Infinity.
 *
 * Returns the cleaned payload typed as Record<string, unknown> — every
 * call site set()s it, and set() takes `unknown`, so the typing stays loose
 * on purpose (we already validated the shape via the input types).
 */
function sanitizeForWrite(payload: Record<string, unknown>, contextLabel: string): Record<string, unknown> {
  const stripped = stripUndefinedDeep(payload, contextLabel);
  assertFiniteNumbers(stripped, contextLabel);
  return stripped as Record<string, unknown>;
}

/** ISO timestamp, single source of truth so every audit entry matches. */
function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Build an audit entry. Centralized so every action has the same shape — the
 * `user` field is the signed-in Firebase user's uid; falls back to 'system'
 * for headless scripts (migrations, battle-test) where no Firebase session
 * exists.
 */
function makeAuditEntry(action: AuditEntry['action'], extras: Partial<AuditEntry> = {}): AuditEntry {
  return {
    ts: nowIso(),
    user: currentFirebaseUser()?.uid ?? 'system',
    action,
    source: 'ui',
    ...extras,
  };
}

/** Append an audit entry to an existing array (handles RTDB's empty-array-strip). */
function appendAudit(existing: AuditEntry[] | undefined, entry: AuditEntry): AuditEntry[] {
  return [...(existing ?? []), entry];
}

/** Stamp baseline fields onto a brand-new entity. id is set by the caller. */
function newBaseline(): Omit<BaselineFields, 'id'> {
  const now = nowIso();
  return {
    createdAt: now,
    updatedAt: now,
    // createdBy / updatedBy — TODO once auth/role flow lands
    _v: 1,
    _audit: [makeAuditEntry('create')], // seed entry so _audit is never empty
    _deleted: false,
  };
}

/** Bump updatedAt; pass-through for incoming updates. */
function withUpdateStamp<T extends Record<string, unknown>>(updates: T): T & { updatedAt: string } {
  return { ...updates, updatedAt: nowIso() };
}

/**
 * Read a single record at a child path, return it with the id key folded in
 * (or null if missing / soft-deleted).
 */
async function readOne<T extends BaselineFields>(
  path: string,
  id: string,
  includeDeleted = false,
): Promise<T | null> {
  const snap = await get(ref(db, `${path}/${id}`));
  if (!snap.exists()) return null;
  const val = snap.val() as Omit<T, 'id'>;
  if (!includeDeleted && (val as { _deleted?: boolean })._deleted) return null;
  return { id, ...val } as T;
}

/**
 * List all children under a path, optionally ordered by a child key.
 * Soft-deleted entries are filtered out by default.
 */
async function readMany<T extends BaselineFields>(
  path: string,
  opts: { orderBy?: string; equalToValue?: string | number | boolean; includeDeleted?: boolean } = {},
): Promise<T[]> {
  const base = ref(db, path);
  let q;
  if (opts.orderBy && opts.equalToValue !== undefined) {
    q = query(base, orderByChild(opts.orderBy), equalTo(opts.equalToValue));
  } else if (opts.orderBy) {
    q = query(base, orderByChild(opts.orderBy));
  } else {
    q = base;
  }
  const snap = await get(q);
  if (!snap.exists()) return [];
  const out: T[] = [];
  snap.forEach((child) => {
    const val = child.val() as Omit<T, 'id'> & { _deleted?: boolean };
    if (!opts.includeDeleted && val._deleted) return false;
    out.push({ id: child.key!, ...val } as T);
    return false;
  });
  return out;
}

/**
 * Internal read-modify-write helper for updateX. Centralizes the
 * snap → merge → sanitize → set flow + appends an `update` audit entry
 * tagged with the changed field names (no values — those balloon fast).
 */
async function readModifyWrite<T extends BaselineFields>(
  path: string,
  id: string,
  entityLabel: string,
  updates: Record<string, unknown>,
): Promise<void> {
  const eRef = ref(db, `${path}/${id}`);
  const snap = await get(eRef);
  if (!snap.exists()) throw new Error(`${entityLabel} ${id} not found`);
  const existing = snap.val() as Omit<T, 'id'>;
  // We log changed field NAMES only — values would balloon the audit array
  // (a 100-part quote update would carry 100 partQuotedSpecs snapshots each
  // time someone tweaks a tier). M7 analytics can diff old vs new from
  // adjacent versions if it ever needs the values.
  const auditEntry = makeAuditEntry('update', {
    metadata: { changedFields: Object.keys(updates) },
  });
  const merged = {
    ...existing,
    ...withUpdateStamp(updates),
    _audit: appendAudit((existing as { _audit?: AuditEntry[] })._audit, auditEntry),
  };
  await set(eRef, sanitizeForWrite(merged as Record<string, unknown>, `${entityLabel}.update`));
}

/**
 * Internal helper for soft-delete / restore. Flips _deleted, appends an
 * audit entry, sanitizes, writes. Bypasses the UpdateXInput type restriction
 * intentionally — these helpers are the ONLY path that should touch
 * BaselineFields directly.
 */
async function writeSoftDeleteFlag<T extends BaselineFields>(
  path: string,
  id: string,
  entityLabel: string,
  deleted: boolean,
  reason?: string,
): Promise<void> {
  const eRef = ref(db, `${path}/${id}`);
  const snap = await get(eRef);
  if (!snap.exists()) throw new Error(`${entityLabel} ${id} not found`);
  const existing = snap.val() as Omit<T, 'id'>;
  const now = nowIso();
  const auditEntry = makeAuditEntry(deleted ? 'soft_delete' : 'restore', {
    metadata: reason ? { reason } : undefined,
  });

  // Build the patch. Restore zeroes out _deletedAt/_deletedBy/_deletedReason
  // by setting them to null (Firebase deletes null keys server-side; we
  // can't use undefined here because sanitizeForWrite strips it before
  // Firebase sees the field, which would leave the stale values in place).
  const patch: Record<string, unknown> = {
    _deleted: deleted,
    updatedAt: now,
    _audit: appendAudit((existing as { _audit?: AuditEntry[] })._audit, auditEntry),
  };
  if (deleted) {
    patch._deletedAt = now;
    patch._deletedBy = currentFirebaseUser()?.uid ?? 'system';
    if (reason !== undefined) patch._deletedReason = reason;
  } else {
    patch._deletedAt = null;
    patch._deletedBy = null;
    patch._deletedReason = null;
  }

  const merged = { ...existing, ...patch };
  await set(
    eRef,
    sanitizeForWrite(merged as Record<string, unknown>, `${entityLabel}.${deleted ? 'softDelete' : 'restore'}`),
  );
}

// =====================================================
// Collection paths — mutable so test runners can namespace under /test/$runId/
// without forking the SDK. See scripts/battle-test wrapper (crm-test.ts) for
// the prod-mirroring contract.
//
// IMPORTANT: app code MUST NOT mutate this object. The crm-test.ts wrapper
// is the only sanctioned mutator (it re-points paths to /test/$runId/ at
// the top of a test run). We do not Object.freeze it because that would
// break the wrapper; treat the warning here as the contract.
// =====================================================

export const CRM_PATHS = {
  customers: 'customers',
  parts: 'parts',
  quotes: 'quotes',
  orders: 'orders',
  quoteTemplates: 'quoteTemplates',
  vendors: 'vendors',
  machines: 'machines',
  materials: 'materials',
};

// =====================================================
// Customers
// =====================================================

function customersRef(): DatabaseReference {
  return ref(db, CRM_PATHS.customers);
}

/** Fields the caller supplies; baseline + id are filled in by us. */
export type CreateCustomerInput = Omit<Customer, keyof BaselineFields>;
export type UpdateCustomerInput = Partial<Omit<Customer, keyof BaselineFields | 'id'>>;

function validateCustomerDateFields(input: Partial<Customer>): void {
  // Allow undefined / empty string; reject malformed.
  assertIsoDate(input.tierLastReviewed, 'Customer.tierLastReviewed');
  assertIsoDate(input.lastContactDate, 'Customer.lastContactDate');
  assertIsoDate(input.nextFollowUpDate, 'Customer.nextFollowUpDate');
  if (input.notes) {
    for (let i = 0; i < input.notes.length; i++) {
      const note = input.notes[i];
      assertIsoDate(note?.ts, `Customer.notes[${i}].ts`);
    }
  }
}

export async function createCustomer(input: CreateCustomerInput): Promise<string> {
  await awaitFirebaseUser();
  validateCustomerDateFields(input);
  const newRef = push(customersRef());
  const id = newRef.key!;
  const record: Omit<Customer, 'id'> = {
    ...newBaseline(),
    ...input,
  };
  await set(newRef, sanitizeForWrite(record as Record<string, unknown>, 'createCustomer'));
  return id;
}

export async function getCustomer(id: string): Promise<Customer | null> {
  await awaitFirebaseUser();
  return readOne<Customer>(CRM_PATHS.customers, id);
}

export interface ListCustomersFilter {
  tier?: CustomerTier;
  includeDeleted?: boolean;
}

export async function listCustomers(filter: ListCustomersFilter = {}): Promise<Customer[]> {
  await awaitFirebaseUser();
  if (filter.tier) {
    return readMany<Customer>(CRM_PATHS.customers, {
      orderBy: 'tier',
      equalToValue: filter.tier,
      includeDeleted: filter.includeDeleted,
    });
  }
  return readMany<Customer>(CRM_PATHS.customers, {
    orderBy: 'updatedAt',
    includeDeleted: filter.includeDeleted,
  });
}

export async function updateCustomer(id: string, updates: UpdateCustomerInput): Promise<void> {
  await awaitFirebaseUser();
  validateCustomerDateFields(updates);
  await readModifyWrite<Customer>(CRM_PATHS.customers, id, 'Customer', updates as Record<string, unknown>);
}

export async function softDeleteCustomer(id: string, reason?: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<Customer>(CRM_PATHS.customers, id, 'Customer', true, reason);
}

export async function restoreCustomer(id: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<Customer>(CRM_PATHS.customers, id, 'Customer', false);
}

/**
 * Substring search across companyName + code (case-insensitive). Client-side
 * filter — Firebase RTDB doesn't do substring queries. Fine for KMF's
 * customer count (hundreds, not millions).
 */
export async function searchCustomers(queryStr: string): Promise<Customer[]> {
  await awaitFirebaseUser();
  const all = await listCustomers();
  const needle = queryStr.trim().toLowerCase();
  if (!needle) return all;
  return all.filter(
    (c) =>
      c.companyName.toLowerCase().includes(needle) ||
      (c.code ?? '').toLowerCase().includes(needle),
  );
}

// =====================================================
// Parts
// =====================================================

function partsRef(): DatabaseReference {
  return ref(db, CRM_PATHS.parts);
}

export type CreatePartInput = Omit<Part, keyof BaselineFields>;
export type UpdatePartInput = Partial<Omit<Part, keyof BaselineFields | 'id'>>;

function validatePartDateFields(input: Partial<Part>): void {
  assertIsoDate(input.materialCost?.snapshotAt, 'Part.materialCost.snapshotAt');
}

export async function createPart(input: CreatePartInput): Promise<string> {
  await awaitFirebaseUser();
  validatePartDateFields(input);
  const newRef = push(partsRef());
  const id = newRef.key!;
  const record: Omit<Part, 'id'> = {
    ...newBaseline(),
    ...input,
  };
  await set(newRef, sanitizeForWrite(record as Record<string, unknown>, 'createPart'));
  return id;
}

export async function getPart(id: string): Promise<Part | null> {
  await awaitFirebaseUser();
  return readOne<Part>(CRM_PATHS.parts, id);
}

export async function listParts(): Promise<Part[]> {
  await awaitFirebaseUser();
  return readMany<Part>(CRM_PATHS.parts, { orderBy: 'updatedAt' });
}

export async function updatePart(id: string, updates: UpdatePartInput): Promise<void> {
  await awaitFirebaseUser();
  validatePartDateFields(updates);
  await readModifyWrite<Part>(CRM_PATHS.parts, id, 'Part', updates as Record<string, unknown>);
}

export async function softDeletePart(id: string, reason?: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<Part>(CRM_PATHS.parts, id, 'Part', true, reason);
}

export async function restorePart(id: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<Part>(CRM_PATHS.parts, id, 'Part', false);
}

// =====================================================
// Quotes
// =====================================================

function quotesRef(): DatabaseReference {
  return ref(db, CRM_PATHS.quotes);
}

export type CreateQuoteInput = Omit<Quote, keyof BaselineFields>;
export type UpdateQuoteInput = Partial<Omit<Quote, keyof BaselineFields | 'id'>>;

/** Options on createX/updateX. validateFks lets migrations bypass FK checks. */
export interface CreateQuoteOptions {
  validateFks?: boolean;
}

function validateQuoteDateFields(input: Partial<Quote>): void {
  assertIsoDate(input.dueDate, 'Quote.dueDate');
  assertIsoDate(input.sentAt, 'Quote.sentAt');
}

export async function createQuote(
  input: CreateQuoteInput,
  options: CreateQuoteOptions = { validateFks: true },
): Promise<string> {
  await awaitFirebaseUser();
  validateQuoteDateFields(input);
  // Cross-check partIds <-> partQuotedSpecs (both present on a create).
  assertPartIdsSpecsConsistent(input.partIds, input.partQuotedSpecs, 'createQuote');

  // FK validation — default on, disabled for migrations.
  if (options.validateFks !== false) {
    if (input.customerId) {
      const cust = await getCustomer(input.customerId);
      if (!cust) {
        throw new Error(`FK violation: customer ${input.customerId} does not exist (createQuote)`);
      }
    }
  }

  const newRef = push(quotesRef());
  const id = newRef.key!;
  const record: Omit<Quote, 'id'> = {
    ...newBaseline(),
    ...input,
  };
  await set(newRef, sanitizeForWrite(record as Record<string, unknown>, 'createQuote'));
  return id;
}

export async function getQuote(id: string): Promise<Quote | null> {
  await awaitFirebaseUser();
  return readOne<Quote>(CRM_PATHS.quotes, id);
}

export interface ListQuotesFilter {
  status?: QuoteStatus;
  customerId?: string;
  includeDeleted?: boolean;
}

export async function listQuotes(filter: ListQuotesFilter = {}): Promise<Quote[]> {
  await awaitFirebaseUser();
  // Firebase RTDB only supports one orderByChild per query — pick the more
  // selective filter, then narrow client-side.
  if (filter.customerId) {
    const byCust = await readMany<Quote>(CRM_PATHS.quotes, {
      orderBy: 'customerId',
      equalToValue: filter.customerId,
      includeDeleted: filter.includeDeleted,
    });
    return filter.status ? byCust.filter((q) => q.status === filter.status) : byCust;
  }
  if (filter.status) {
    return readMany<Quote>(CRM_PATHS.quotes, {
      orderBy: 'status',
      equalToValue: filter.status,
      includeDeleted: filter.includeDeleted,
    });
  }
  return readMany<Quote>(CRM_PATHS.quotes, {
    orderBy: 'updatedAt',
    includeDeleted: filter.includeDeleted,
  });
}

export async function listQuotesByCustomer(customerId: string): Promise<Quote[]> {
  return listQuotes({ customerId });
}

export async function updateQuote(id: string, updates: UpdateQuoteInput): Promise<void> {
  await awaitFirebaseUser();
  validateQuoteDateFields(updates);

  // If either field is in the update we have to re-check consistency. We need
  // the MERGED view — the update might supply only one half (e.g. add a partId
  // alongside the existing spec entry, or vice versa). Fetch existing first
  // when only one side is in the patch.
  const touchesPartIds = updates.partIds !== undefined;
  const touchesSpecs = updates.partQuotedSpecs !== undefined;
  if (touchesPartIds || touchesSpecs) {
    let nextPartIds = updates.partIds;
    let nextSpecs = updates.partQuotedSpecs;
    if (!touchesPartIds || !touchesSpecs) {
      const existing = await getQuote(id);
      if (!existing) throw new Error(`Quote ${id} not found`);
      if (!touchesPartIds) nextPartIds = existing.partIds;
      if (!touchesSpecs) nextSpecs = existing.partQuotedSpecs;
    }
    assertPartIdsSpecsConsistent(nextPartIds, nextSpecs, `updateQuote(${id})`);
  }

  await readModifyWrite<Quote>(CRM_PATHS.quotes, id, 'Quote', updates as Record<string, unknown>);
}

/**
 * Move a quote to a new status. Mirror of transitionOrderStatus. Stamps the
 * new status, appends a status_change audit entry, validates the transition
 * is allowed per QUOTE_STATUS_TRANSITIONS.
 */
export async function transitionQuoteStatus(
  id: string,
  newStatus: QuoteStatus,
  reason?: string,
): Promise<void> {
  await awaitFirebaseUser();
  const qRef = ref(db, `${CRM_PATHS.quotes}/${id}`);
  const snap = await get(qRef);
  if (!snap.exists()) throw new Error(`Quote ${id} not found`);
  const existing = snap.val() as Omit<Quote, 'id'>;
  assertQuoteTransition(id, existing.status, newStatus);
  const now = nowIso();
  const auditEntry = makeAuditEntry('status_change', {
    fields: { status: { from: existing.status, to: newStatus } },
    metadata: reason ? { reason } : undefined,
  });
  const merged: Omit<Quote, 'id'> = {
    ...existing,
    status: newStatus,
    updatedAt: now,
    _audit: appendAudit(existing._audit, auditEntry),
  };
  await set(qRef, sanitizeForWrite(merged as Record<string, unknown>, 'transitionQuoteStatus'));
}

export async function softDeleteQuote(id: string, reason?: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<Quote>(CRM_PATHS.quotes, id, 'Quote', true, reason);
}

export async function restoreQuote(id: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<Quote>(CRM_PATHS.quotes, id, 'Quote', false);
}

// =====================================================
// Orders
// =====================================================

function ordersRef(): DatabaseReference {
  return ref(db, CRM_PATHS.orders);
}

export type CreateOrderInput = Omit<Order, keyof BaselineFields>;
export type UpdateOrderInput = Partial<Omit<Order, keyof BaselineFields | 'id'>>;

export interface CreateOrderOptions {
  validateFks?: boolean;
}

function validateOrderDateFields(input: Partial<Order>): void {
  assertIsoDate(input.statusChangedAt, 'Order.statusChangedAt');
  assertIsoDate(input.poReceivedAt, 'Order.poReceivedAt');
  assertIsoDate(input.promisedShipDate, 'Order.promisedShipDate');
  assertIsoDate(input.actualShipDate, 'Order.actualShipDate');
}

export async function createOrder(
  input: CreateOrderInput,
  options: CreateOrderOptions = { validateFks: true },
): Promise<string> {
  await awaitFirebaseUser();
  validateOrderDateFields(input);

  if (options.validateFks !== false) {
    if (input.customerId) {
      const cust = await getCustomer(input.customerId);
      if (!cust) {
        throw new Error(`FK violation: customer ${input.customerId} does not exist (createOrder)`);
      }
    }
    if (input.fromQuoteId) {
      const quote = await getQuote(input.fromQuoteId);
      if (!quote) {
        throw new Error(`FK violation: quote ${input.fromQuoteId} does not exist (createOrder)`);
      }
    }
  }

  const newRef = push(ordersRef());
  const id = newRef.key!;
  const record: Omit<Order, 'id'> = {
    ...newBaseline(),
    ...input,
  };
  await set(newRef, sanitizeForWrite(record as Record<string, unknown>, 'createOrder'));
  return id;
}

export async function getOrder(id: string): Promise<Order | null> {
  await awaitFirebaseUser();
  return readOne<Order>(CRM_PATHS.orders, id);
}

export interface ListOrdersFilter {
  status?: OrderStatus;
  customerId?: string;
  includeDeleted?: boolean;
}

export async function listOrders(filter: ListOrdersFilter = {}): Promise<Order[]> {
  await awaitFirebaseUser();
  if (filter.customerId) {
    const byCust = await readMany<Order>(CRM_PATHS.orders, {
      orderBy: 'customerId',
      equalToValue: filter.customerId,
      includeDeleted: filter.includeDeleted,
    });
    return filter.status ? byCust.filter((o) => o.status === filter.status) : byCust;
  }
  if (filter.status) {
    return readMany<Order>(CRM_PATHS.orders, {
      orderBy: 'status',
      equalToValue: filter.status,
      includeDeleted: filter.includeDeleted,
    });
  }
  return readMany<Order>(CRM_PATHS.orders, {
    orderBy: 'updatedAt',
    includeDeleted: filter.includeDeleted,
  });
}

export async function updateOrder(id: string, updates: UpdateOrderInput): Promise<void> {
  await awaitFirebaseUser();
  validateOrderDateFields(updates);
  await readModifyWrite<Order>(CRM_PATHS.orders, id, 'Order', updates as Record<string, unknown>);
}

/**
 * Move an order to a new status. Stamps `statusChangedAt`, validates the
 * transition is allowed per ORDER_STATUS_TRANSITIONS, and writes a
 * `status_change` audit entry.
 */
export async function transitionOrderStatus(
  id: string,
  newStatus: OrderStatus,
  reason?: string,
): Promise<void> {
  await awaitFirebaseUser();
  const oRef = ref(db, `${CRM_PATHS.orders}/${id}`);
  const snap = await get(oRef);
  if (!snap.exists()) throw new Error(`Order ${id} not found`);
  const existing = snap.val() as Omit<Order, 'id'>;
  assertOrderTransition(id, existing.status, newStatus);
  const now = nowIso();
  const auditEntry = makeAuditEntry('status_change', {
    fields: { status: { from: existing.status, to: newStatus } },
    metadata: reason ? { reason } : undefined,
  });
  const merged: Omit<Order, 'id'> = {
    ...existing,
    status: newStatus,
    statusChangedAt: now,
    updatedAt: now,
    _audit: appendAudit(existing._audit, auditEntry),
  };
  await set(oRef, sanitizeForWrite(merged as Record<string, unknown>, 'transitionOrderStatus'));
}

export async function softDeleteOrder(id: string, reason?: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<Order>(CRM_PATHS.orders, id, 'Order', true, reason);
}

export async function restoreOrder(id: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<Order>(CRM_PATHS.orders, id, 'Order', false);
}

// =====================================================
// Quote templates
// =====================================================

function quoteTemplatesRef(): DatabaseReference {
  return ref(db, CRM_PATHS.quoteTemplates);
}

export type CreateQuoteTemplateInput = Omit<QuoteTemplate, keyof BaselineFields>;
export type UpdateQuoteTemplateInput = Partial<Omit<QuoteTemplate, keyof BaselineFields | 'id'>>;

export async function createQuoteTemplate(input: CreateQuoteTemplateInput): Promise<string> {
  await awaitFirebaseUser();
  const newRef = push(quoteTemplatesRef());
  const id = newRef.key!;
  const record: Omit<QuoteTemplate, 'id'> = {
    ...newBaseline(),
    ...input,
  };
  await set(newRef, sanitizeForWrite(record as Record<string, unknown>, 'createQuoteTemplate'));
  return id;
}

export async function getQuoteTemplate(id: string): Promise<QuoteTemplate | null> {
  await awaitFirebaseUser();
  return readOne<QuoteTemplate>(CRM_PATHS.quoteTemplates, id);
}

export interface ListQuoteTemplatesFilter {
  customerId?: string;
  includeDeleted?: boolean;
}

export async function listQuoteTemplates(
  filter: ListQuoteTemplatesFilter = {},
): Promise<QuoteTemplate[]> {
  await awaitFirebaseUser();
  if (filter.customerId) {
    return readMany<QuoteTemplate>(CRM_PATHS.quoteTemplates, {
      orderBy: 'customerId',
      equalToValue: filter.customerId,
      includeDeleted: filter.includeDeleted,
    });
  }
  return readMany<QuoteTemplate>(CRM_PATHS.quoteTemplates, {
    orderBy: 'name',
    includeDeleted: filter.includeDeleted,
  });
}

export async function updateQuoteTemplate(
  id: string,
  updates: UpdateQuoteTemplateInput,
): Promise<void> {
  await awaitFirebaseUser();
  await readModifyWrite<QuoteTemplate>(
    CRM_PATHS.quoteTemplates,
    id,
    'QuoteTemplate',
    updates as Record<string, unknown>,
  );
}

export async function softDeleteQuoteTemplate(id: string, reason?: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<QuoteTemplate>(
    CRM_PATHS.quoteTemplates,
    id,
    'QuoteTemplate',
    true,
    reason,
  );
}

export async function restoreQuoteTemplate(id: string): Promise<void> {
  await awaitFirebaseUser();
  await writeSoftDeleteFlag<QuoteTemplate>(
    CRM_PATHS.quoteTemplates,
    id,
    'QuoteTemplate',
    false,
  );
}
